// ─── src/context/AuraProvider.jsx ────────────────────────────────────────────
// AuraProvider mounts the AuraCore side effects that need React's lifecycle.
//
// Effect 1: messages change     → load article recommendations
// Effect 2: telemetry flush     → drain analyticsBuffer to Supabase
//
// Venue recommendations are owned exclusively by the orchestrator (Doctrine 1).
// AuraCore.sendMessage populates recommendations on every query turn.
// The panel starts empty on page load, no fallback recommendation calls.
//
// Owns zero state. All state lives in the AuraCore Zustand store.

import { useEffect } from 'react';
import { useAura } from '../stores/auraCore';
import { supabase } from '../lib/supabaseClient';

const FLUSH_INTERVAL_MS = 30_000; // flush buffered events every 30s

// ─── Service helpers (lazy-imported to keep initial bundle lean) ──────────────

// Keyword → article category mapping (vendor intent)
const VENDOR_TO_ARTICLE_CAT = {
  photographer: 'photography', photography: 'photography',
  planner: 'planning', planning: 'planning',
  florist: 'style', flowers: 'style',
  caterer: 'style', catering: 'style',
  musician: 'style', entertainment: 'style',
  celebrant: 'style',
};

// Keyword → destination slug mapping (region intent)
const REGION_TO_DESTINATION = {
  tuscany: 'tuscany', florence: 'tuscany', siena: 'tuscany',
  amalfi: 'amalfi-coast', campania: 'amalfi-coast', positano: 'amalfi-coast',
  'lake como': 'lake-como', lombardy: 'lake-como', como: 'lake-como',
  sicily: 'sicily', palermo: 'sicily',
  venice: 'venice', veneto: 'venice',
  rome: 'rome', lazio: 'rome',
  puglia: 'puglia', lecce: 'puglia',
};

async function loadArticleRecs(messages) {
  try {
    if (!messages || messages.length < 2) return [];

    const text = messages
      .filter((m) => m.from === 'user')
      .map((m) => m.text ?? m.content ?? '')
      .join(' ')
      .toLowerCase();

    let articleCategory = null;
    let tags = [];

    for (const [kw, cat] of Object.entries(VENDOR_TO_ARTICLE_CAT)) {
      if (text.includes(kw)) { articleCategory = cat; break; }
    }

    if (!articleCategory) {
      for (const [kw, slug] of Object.entries(REGION_TO_DESTINATION)) {
        if (text.includes(kw)) {
          articleCategory = 'destinations';
          tags = [slug];
          break;
        }
      }
    }

    if (!articleCategory) return [];

    const { getArticleRecommendationsForIntent } = await import('../services/recommendationEngine');
    const { articles } = await getArticleRecommendationsForIntent({ articleCategory, tags, maxResults: 3 });
    return articles || [];
  } catch {
    return [];
  }
}

async function flushEventsToSupabase(events) {
  if (!events.length) return;
  try {
    const { supabase } = await import('../lib/supabaseClient');
    await supabase.from('aura_events').insert(
      events.map((e) => ({
        type:       e.type,
        payload:    e.payload,
        session_id: e.sessionId,
        device:     e.device,
        ts:         e.timestamp,
      }))
    );
  } catch (err) {
    // Telemetry failures are non-fatal, log and continue
    console.warn('[AuraProvider] telemetry flush failed:', err?.message);
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuraProvider({ children }) {
  const messages                  = useAura((s) => s.messages);
  const setArticleRecommendations = useAura((s) => s.setArticleRecommendations);
  const flushAnalytics            = useAura((s) => s.flushAnalytics);
  const hydrateWedding            = useAura((s) => s.hydrateWedding);

  // ── Resume: load an existing active Wedding on mount (the "welcome back").
  // No-op unless the Wedding entity feature is enabled (flag-gated in the store).
  useEffect(() => { hydrateWedding?.(); }, [hydrateWedding]);

  // Re-hydrate on any auth change so the panel always reflects the CURRENT owner
  //, an upgraded anonymous user keeps the same wedding; sign-out clears it.
  useEffect(() => {
    const sub = supabase?.auth?.onAuthStateChange?.(() => { hydrateWedding?.(); });
    return () => sub?.data?.subscription?.unsubscribe?.();
  }, [hydrateWedding]);

  // ── Effect 1: article recommendations on conversation change ──────────────
  useEffect(() => {
    if (messages.length < 2) {
      setArticleRecommendations([]);
      return;
    }
    let cancelled = false;
    loadArticleRecs(messages).then((articles) => {
      if (!cancelled) setArticleRecommendations(articles);
    });
    return () => { cancelled = true; };
  }, [messages, setArticleRecommendations]);

  // ── Effect 2: telemetry flush ─────────────────────────────────────────────
  // Drains the analyticsBuffer to Supabase on a 30s interval,
  // on page hide (tab switch/close), and on provider unmount.
  useEffect(() => {
    const flush = () => flushEventsToSupabase(flushAnalytics());

    const intervalId = setInterval(flush, FLUSH_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      flush(); // drain on unmount
    };
  }, [flushAnalytics]);

  return children;
}
